import {type AppBskyVideoDefs, type BskyAgent} from '@atproto/api'
import {type I18n} from '@lingui/core'
import {msg} from '@lingui/macro'
import {nanoid} from 'nanoid/non-secure'

import {AbortError} from '#/lib/async/cancelable'
import {ServerError} from '#/lib/media/video/errors'
import {type CompressedVideo} from '#/lib/media/video/types'
import {
  getUploadVideoAuthToken,
  getVideoUploadLimits,
  normalizeVideoMime,
} from './upload.shared'
import {createVideoEndpointUrl, mimeToExt} from './util'

type UploadDebugInfo = {
  url: string
  mimeType: string
  size: number
  uploadName: string
  target: 'direct' | 'proxy'
}

type UploadErrorDetails = {
  status?: number
  statusText?: string
  url?: string
  responseHeaders?: string
  responseTextPreview?: string
  responseTextLength?: number
  data?: unknown
  isNetworkError?: boolean
}

function parseJobStatus(
  payload: unknown,
): AppBskyVideoDefs.JobStatus | null {
  if (!payload || typeof payload !== 'object') return null
  const data = payload as {
    jobStatus?: AppBskyVideoDefs.JobStatus
    jobId?: string
  }
  if (data.jobStatus && typeof data.jobStatus === 'object') {
    return data.jobStatus
  }
  if (typeof data.jobId === 'string') {
    return payload as AppBskyVideoDefs.JobStatus
  }
  return null
}

function extractServerError(payload: unknown) {
  if (!payload || typeof payload !== 'object') return undefined
  const data = payload as {message?: unknown; error?: unknown}
  if (typeof data.message === 'string') return data.message
  if (typeof data.error === 'string') return data.error
  return undefined
}

function getResponseHeaders(xhr: XMLHttpRequest) {
  try {
    return xhr.getAllResponseHeaders()
  } catch {
    return ''
  }
}

function previewText(text: string, limit = 200) {
  if (!text) return ''
  return text.length > limit ? `${text.slice(0, limit)}…` : text
}

function buildUploadErrorMessage(
  status: number,
  statusText: string,
  serverError: string | undefined,
  _: I18n['_'],
) {
  if (
    serverError &&
    serverError.toLowerCase().includes('invalid token lexicon method')
  ) {
    return serverError
  }
  if (
    status === 401 &&
    serverError &&
    serverError.toLowerCase().includes('invalid token audience')
  ) {
    return _(
      msg`Upload rejected: token audience mismatch. Please re-authenticate.`,
    )
  }
  if (status === 0) {
    return _(
      msg`Network error while uploading video. This can be caused by CORS or HTTP/2 issues.`,
    )
  }
  if (status === 401 || status === 403) {
    return _(msg`Upload rejected. Please re-authenticate.`)
  }
  if (status === 413) {
    return _(msg`Upload rejected: file too large.`)
  }
  if (serverError) {
    return serverError
  }
  if (statusText) {
    return `${_(msg`Failed to upload video`)} (${status} ${statusText})`
  }
  return status
    ? `${_(msg`Failed to upload video`)} (HTTP ${status})`
    : _(msg`Failed to upload video`)
}

function logDev(event: string, data: Record<string, unknown>) {
  if (!__DEV__) return
  console.info('[video-upload]', event, data)
}

function makeUploadError(message: string, details: UploadErrorDetails) {
  const error = new ServerError(message)
  Object.assign(error, details)
  return error
}

async function uploadViaFetch({
  uri,
  payload,
  token,
  contentType,
  signal,
  _,
  debugInfo,
}: {
  uri: string
  payload: Blob
  token: string
  contentType: string
  signal: AbortSignal
  _: I18n['_']
  debugInfo: UploadDebugInfo
}): Promise<AppBskyVideoDefs.JobStatus> {
  logDev('fetch-start', debugInfo)
  let response: Response
  try {
    response = await fetch(uri, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': contentType,
      },
      body: payload,
      signal,
    })
  } catch (err) {
    throw makeUploadError(_(msg`Network error while uploading video.`), {
      isNetworkError: true,
      url: uri,
      data: __DEV__ ? err : undefined,
    })
  }

  const responseText = await response.text()
  let parsed: unknown = null
  try {
    parsed = JSON.parse(responseText)
  } catch {
    parsed = null
  }
  const jobStatus = parseJobStatus(parsed)
  const serverError = extractServerError(parsed)
  const isAlreadyExists =
    response.status === 409 &&
    parsed &&
    typeof parsed === 'object' &&
    (parsed as {error?: string}).error === 'already_exists' &&
    (parsed as {jobId?: string}).jobId

  if (isAlreadyExists) {
    return parsed as AppBskyVideoDefs.JobStatus
  }

  const preview = previewText(responseText)
  logDev('fetch-response', {
    status: response.status,
    statusText: response.statusText,
    headers: __DEV__ ? Object.fromEntries(response.headers.entries()) : undefined,
    responseTextLength: responseText.length,
    responseTextPreview: preview,
  })

  if (!response.ok || !jobStatus) {
    throw makeUploadError(
      buildUploadErrorMessage(
        response.status,
        response.statusText,
        serverError,
        _,
      ),
      {
        status: response.status,
        statusText: response.statusText,
        url: uri,
        responseTextPreview: preview,
        responseTextLength: responseText.length,
        data: __DEV__ ? parsed : undefined,
      },
    )
  }

  return jobStatus
}

function getUploadTargetUrl({
  did,
  uploadName,
}: {
  did: string
  uploadName: string
}): {url: string; target: 'direct' | 'proxy'} {
  const directUploadEnabled =
    typeof process !== 'undefined' &&
    (process.env.EXPO_PUBLIC_VIDEO_DIRECT_UPLOAD ??
      (process.env.NODE_ENV === 'production' ? '1' : '0')) === '1'
  const isHttpsOrigin =
    typeof window !== 'undefined' && window.location?.protocol === 'https:'
  if (directUploadEnabled && isHttpsOrigin) {
    return {
      url: createVideoEndpointUrl('/xrpc/app.bsky.video.uploadVideo', {
        did,
        name: uploadName,
      }),
      target: 'direct',
    }
  }

  if (typeof window === 'undefined') {
    return {
      url: createVideoEndpointUrl('/xrpc/app.bsky.video.uploadVideo', {
        did,
        name: uploadName,
      }),
      target: 'direct',
    }
  }

  const proxyUrl = new URL('/__proxy/video/upload', window.location.origin)
  proxyUrl.searchParams.set('did', did)
  proxyUrl.searchParams.set('name', uploadName)
  return {url: proxyUrl.toString(), target: 'proxy'}
}

async function uploadViaXhr({
  uri,
  payload,
  token,
  contentType,
  setProgress,
  signal,
  _,
  debugInfo,
}: {
  uri: string
  payload: Blob
  token: string
  contentType: string
  setProgress: (progress: number) => void
  signal: AbortSignal
  _: I18n['_']
  debugInfo: UploadDebugInfo
}): Promise<AppBskyVideoDefs.JobStatus> {
  logDev('xhr-start', debugInfo)
  const xhr = new XMLHttpRequest()
  return new Promise((resolve, reject) => {
    let settled = false
    const resolveOnce = (value: AppBskyVideoDefs.JobStatus) => {
      if (settled) return
      settled = true
      resolve(value)
    }
    const rejectOnce = (error: Error) => {
      if (settled) return
      settled = true
      reject(error)
    }

    const finishWithError = (message: string, details: UploadErrorDetails) => {
      rejectOnce(makeUploadError(message, details))
    }

    xhr.upload.addEventListener('progress', e => {
      const progress = e.total ? e.loaded / e.total : 0
      setProgress(progress)
    })

    xhr.onloadend = () => {
      if (settled) return
      if (signal.aborted) {
        rejectOnce(new AbortError())
        return
      }
      const responseText = xhr.responseText || ''
      let parsed: unknown = null
      try {
        parsed = JSON.parse(responseText)
      } catch {
        parsed = null
      }
      const jobStatus = parseJobStatus(parsed)
      const serverError = extractServerError(parsed)
      const isAlreadyExists =
        xhr.status === 409 &&
        parsed &&
        typeof parsed === 'object' &&
        (parsed as {error?: string}).error === 'already_exists' &&
        (parsed as {jobId?: string}).jobId

      const headers = getResponseHeaders(xhr)
      const preview = previewText(responseText)
      logDev('xhr-loadend', {
        readyState: xhr.readyState,
        status: xhr.status,
        statusText: xhr.statusText,
        responseHeaders: headers,
        responseTextLength: responseText.length,
        responseTextPreview: preview,
      })

      if ((xhr.status >= 200 && xhr.status < 300 && jobStatus) || isAlreadyExists) {
        resolveOnce(isAlreadyExists ? (parsed as AppBskyVideoDefs.JobStatus) : jobStatus)
        return
      }

      finishWithError(
        buildUploadErrorMessage(xhr.status, xhr.statusText, serverError, _),
        {
          status: xhr.status,
          statusText: xhr.statusText,
          url: uri,
          responseHeaders: headers,
          responseTextPreview: preview,
          responseTextLength: responseText.length,
          data: __DEV__ ? parsed : undefined,
          isNetworkError: xhr.status === 0,
        },
      )
    }

    xhr.onerror = () => {
      if (settled) return
      logDev('xhr-error', {
        readyState: xhr.readyState,
        status: xhr.status,
        statusText: xhr.statusText,
      })
      finishWithError(_(msg`Network error while uploading video.`), {
        status: xhr.status,
        statusText: xhr.statusText,
        url: uri,
        responseHeaders: getResponseHeaders(xhr),
        responseTextPreview: previewText(xhr.responseText || ''),
        responseTextLength: xhr.responseText?.length || 0,
        isNetworkError: true,
      })
    }

    xhr.onabort = () => {
      if (settled) return
      logDev('xhr-abort', {readyState: xhr.readyState, status: xhr.status})
      rejectOnce(new AbortError())
    }

    xhr.ontimeout = () => {
      if (settled) return
      logDev('xhr-timeout', {readyState: xhr.readyState, status: xhr.status})
      finishWithError(_(msg`Video upload timed out.`), {
        status: xhr.status,
        statusText: xhr.statusText,
        url: uri,
      })
    }

    if (signal.aborted) {
      rejectOnce(new AbortError())
      return
    }
    signal.addEventListener(
      'abort',
      () => {
        xhr.abort()
      },
      {once: true},
    )
    xhr.open('POST', uri)
    xhr.setRequestHeader('Authorization', `Bearer ${token}`)
    xhr.setRequestHeader('Content-Type', contentType)
    xhr.send(payload)
  })
}

export async function uploadVideo({
  video,
  agent,
  did,
  setProgress,
  signal,
  _,
}: {
  video: CompressedVideo
  agent: BskyAgent
  did: string
  setProgress: (progress: number) => void
  signal: AbortSignal
  _: I18n['_']
}) {
  if (signal.aborted) {
    throw new AbortError()
  }
  await getVideoUploadLimits(agent, _)

  const normalizedMime = normalizeVideoMime(video.mimeType)
  const uploadName = `${nanoid(12)}.${mimeToExt(normalizedMime)}`
  const {url: uri, target} = getUploadTargetUrl({did, uploadName})

  let bytes = video.bytes
  if (!bytes) {
    if (signal.aborted) {
      throw new AbortError()
    }
    bytes = await fetch(video.uri).then(res => res.arrayBuffer())
  }

  if (signal.aborted) {
    throw new AbortError()
  }
  const token = await getUploadVideoAuthToken({
    agent,
    exp: Math.floor(Date.now() / 1000) + 60 * 30, // 30 minutes
  })

  if (signal.aborted) {
    throw new AbortError()
  }
  const payload = new Blob([bytes], {type: normalizedMime})
  const debugInfo: UploadDebugInfo = {
    url: uri,
    mimeType: normalizedMime,
    size: payload.size,
    uploadName,
    target,
  }

  const forceFetchUpload =
    __DEV__ &&
    typeof window !== 'undefined' &&
    window.localStorage?.getItem('tellustv:forceFetchUpload') === '1'

  let jobStatus: AppBskyVideoDefs.JobStatus
  try {
    if (forceFetchUpload) {
      logDev('force-fetch', {reason: 'tellustv:forceFetchUpload'})
      setProgress(0.01)
      jobStatus = await uploadViaFetch({
        uri,
        payload,
        token,
        contentType: normalizedMime,
        signal,
        _,
        debugInfo,
      })
    } else {
      jobStatus = await uploadViaXhr({
        uri,
        payload,
        token,
        contentType: normalizedMime,
        setProgress,
        signal,
        _,
        debugInfo,
      })
    }
  } catch (err) {
    if (err instanceof AbortError) {
      throw err
    }
    const status =
      err && typeof err === 'object' ? (err as {status?: number}).status : undefined
    const isNetworkError =
      err && typeof err === 'object'
        ? (err as {isNetworkError?: boolean}).isNetworkError
        : false
    if ((status === 0 || isNetworkError) && !forceFetchUpload) {
      // Some browsers report HTTP/2 protocol errors for XHR binary uploads.
      logDev('xhr-fallback-fetch', {reason: 'network-error'})
      setProgress(0.01)
      jobStatus = await uploadViaFetch({
        uri,
        payload,
        token,
        contentType: normalizedMime,
        signal,
        _,
        debugInfo,
      })
    } else {
      throw err
    }
  }

  if (!jobStatus?.jobId) {
    throw new ServerError(jobStatus?.error || _(msg`Failed to upload video`))
  }

  if (signal.aborted) {
    throw new AbortError()
  }
  return jobStatus
}
